import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Building2, List, Clock, ClipboardList, MessageSquare, UserPlus, Flag, Loader2,
  Check, X, Ban, Star, StarOff, Eye, Plus, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingState, EmptyState } from "@/components/States";
import { LISTING_STATUS } from "@/lib/constants";
import { priceLabel, formatDate, timeAgo } from "@/lib/format";
import { imageUrl } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export function AdminOverview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setS(data)); }, []);
  const cards = [
    { label: "Toplam Kullanıcı", value: s?.total_users, icon: Users, color: "bg-blue-50 text-blue-600" },
    { label: "Kurumsal Kullanıcı", value: s?.corporate_users, icon: Building2, color: "bg-indigo-50 text-indigo-600" },
    { label: "Aktif İlan", value: s?.active_listings, icon: List, color: "bg-emerald-50 text-emerald-600" },
    { label: "Bekleyen İlan", value: s?.pending_listings, icon: Clock, color: "bg-amber-50 text-amber-600" },
    { label: "Alım Talebi", value: s?.buy_requests, icon: ClipboardList, color: "bg-purple-50 text-purple-600" },
    { label: "Mesaj", value: s?.messages, icon: MessageSquare, color: "bg-cyan-50 text-cyan-600" },
    { label: "Yeni Kayıt (7g)", value: s?.new_registrations, icon: UserPlus, color: "bg-teal-50 text-teal-600" },
    { label: "Açık Şikayet", value: s?.open_reports, icon: Flag, color: "bg-red-50 text-red-600" },
  ];
  return (
    <div>
      <h1 className="font-display text-2xl font-extrabold text-slate-900">Genel Bakış</h1>
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`admin-stat-${c.label}`}>
            <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${c.color}`}><c.icon size={20} /></span>
            <p className="mt-3 text-3xl font-extrabold text-slate-900">{s ? (c.value ?? 0) : "–"}</p>
            <p className="text-sm text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>
      {s?.pending_listings > 0 && (
        <Link to="/admin/ilanlar" className="mt-6 flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
          <span className="font-medium">{s.pending_listings} ilan onay bekliyor</span><Button size="sm" variant="outline">İncele</Button>
        </Link>
      )}
      {s?.pending_corporate > 0 && (
        <Link to="/admin/kurumsal" className="mt-3 flex items-center justify-between rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-indigo-800">
          <span className="font-medium">{s.pending_corporate} kurumsal hesap onay bekliyor</span><Button size="sm" variant="outline">İncele</Button>
        </Link>
      )}
    </div>
  );
}

export function AdminUsers() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const load = (query = "") => api.get(`/admin/users${query ? `?q=${query}` : ""}`).then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const suspend = async (id, suspended) => { await api.patch(`/admin/users/${id}/suspend`, { suspended }); toast.success(suspended ? "Kullanıcı askıya alındı" : "Askı kaldırıldı"); load(q); };
  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Kullanıcılar</h1>
      <div className="mb-4 flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)} placeholder="İsim veya e-posta ara..." data-testid="admin-user-search" />
        <Button onClick={() => load(q)} className="bg-emerald-600 hover:bg-emerald-700">Ara</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {items.map((u) => (
          <div key={u.user_id} className="flex items-center justify-between gap-3 border-b border-slate-100 p-3 last:border-0" data-testid={`admin-user-${u.user_id}`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">{u.name} {u.role === "admin" && <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700">ADMIN</span>}</p>
              <p className="truncate text-xs text-slate-500">{u.email} • {u.account_type} • {u.city || "—"}</p>
            </div>
            {u.role !== "admin" && (
              u.is_suspended ? (
                <Button size="sm" variant="outline" onClick={() => suspend(u.user_id, false)} data-testid={`unsuspend-${u.user_id}`}>Askıyı Kaldır</Button>
              ) : (
                <Button size="sm" variant="outline" className="gap-1 text-red-600" onClick={() => suspend(u.user_id, true)} data-testid={`suspend-${u.user_id}`}><Ban size={14} /> Askıya Al</Button>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminCorporate() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/corporate").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const approve = async (id, approved) => { await api.patch(`/admin/corporate/${id}/approve`, { approved }); toast.success(approved ? "Onaylandı" : "Onay kaldırıldı"); load(); };
  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Kurumsal Hesaplar</h1>
      {items.length === 0 ? <EmptyState title="Kurumsal hesap yok" icon={Building2} /> : (
        <div className="space-y-3">
          {items.map((u) => (
            <div key={u.user_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-corp-${u.user_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-800">{u.company?.company_name || u.name}</p>
                  <p className="text-xs text-slate-500">{u.email} • VKN: {u.company?.tax_number || "—"} • {u.city || "—"}</p>
                  {u.company?.company_description && <p className="mt-1 text-sm text-slate-600">{u.company.company_description}</p>}
                </div>
                <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold", u.corporate_approved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}>
                  {u.corporate_approved ? "Onaylı" : "Bekliyor"}
                </span>
              </div>
              <div className="mt-3 flex gap-2">
                {u.corporate_approved ? (
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => approve(u.user_id, false)} data-testid={`revoke-corp-${u.user_id}`}>Onayı Kaldır</Button>
                ) : (
                  <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => approve(u.user_id, true)} data-testid={`approve-corp-${u.user_id}`}><Check size={14} /> Onayla</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminListings() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("onay_bekliyor");
  const load = (status) => api.get(`/admin/listings${status !== "all" ? `?status=${status}` : ""}`).then(({ data }) => setItems(data.items));
  useEffect(() => { load(filter); }, [filter]);
  const moderate = async (id, action, reason) => { await api.patch(`/admin/listings/${id}/moderate`, { action, reason }); toast.success("İşlem tamamlandı"); load(filter); };
  const feature = async (id, featured) => { await api.patch(`/admin/listings/${id}/feature`, { featured }); toast.success("Güncellendi"); load(filter); };
  if (items === null) return <LoadingState />;
  const FILTERS = [["onay_bekliyor", "Onay Bekleyen"], ["aktif", "Aktif"], ["reddedildi", "Reddedilen"], ["pasif", "Pasif"], ["all", "Tümü"]];
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">İlan Yönetimi</h1>
      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`admin-filter-${v}`}
            className={cn("rounded-full border px-3 py-1.5 text-sm font-medium", filter === v ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600")}>{l}</button>
        ))}
      </div>
      {items.length === 0 ? <EmptyState title="İlan yok" icon={List} /> : (
        <div className="space-y-3">
          {items.map((l) => {
            const st = LISTING_STATUS[l.status] || LISTING_STATUS.pasif;
            return (
              <div key={l.listing_id} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center" data-testid={`admin-listing-${l.listing_id}`}>
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-100">{l.images?.[0] && <img src={imageUrl(l.images[0])} alt="" className="h-full w-full object-cover" />}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Link to={`/ilan/${l.listing_id}`} className="truncate font-medium text-slate-800 hover:text-emerald-700">{l.title}</Link>
                    {l.is_featured && <Star size={14} className="shrink-0 fill-amber-400 text-amber-400" />}
                  </div>
                  <p className="text-sm font-semibold text-emerald-900">{priceLabel(l)}</p>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <span className={`rounded-full px-2 py-0.5 font-semibold ${st.cls}`}>{st.label}</span>
                    <span className="text-slate-400">{l.seller_name} • {l.city}</span>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {l.status === "onay_bekliyor" && (
                    <>
                      <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => moderate(l.listing_id, "approve")} data-testid={`approve-listing-${l.listing_id}`}><Check size={14} /> Onayla</Button>
                      <RejectDialog onReject={(r) => moderate(l.listing_id, "reject", r)} listingId={l.listing_id} />
                    </>
                  )}
                  {l.status === "aktif" && (
                    <>
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => feature(l.listing_id, !l.is_featured)} data-testid={`feature-${l.listing_id}`}>
                        {l.is_featured ? <StarOff size={14} /> : <Star size={14} />}{l.is_featured ? "Kaldır" : "Öne Çıkar"}
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => moderate(l.listing_id, "suspend")} data-testid={`suspend-listing-${l.listing_id}`}>Askıya Al</Button>
                    </>
                  )}
                  {(l.status === "pasif" || l.status === "reddedildi") && (
                    <Button size="sm" variant="outline" onClick={() => moderate(l.listing_id, "activate")}>Yayınla</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RejectDialog({ onReject, listingId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="gap-1 text-red-600" data-testid={`reject-listing-${listingId}`}><X size={14} /> Reddet</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>İlanı Reddet</DialogTitle></DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Red nedeni (satıcıya iletilir)" rows={3} data-testid="reject-reason" />
        <DialogFooter><Button variant="destructive" onClick={() => { onReject(reason); setOpen(false); }}>Reddet</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminCategories() {
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ name: "", slug: "", icon: "Package", subcategories: "" });
  const load = () => api.get("/categories").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post("/admin/categories", { name: f.name, slug: f.slug, icon: f.icon, order: 99, subcategories: f.subcategories.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Kategori eklendi"); setF({ name: "", slug: "", icon: "Package", subcategories: "" }); load();
    } catch (e) { toast.error("Eklenemedi"); }
  };
  const remove = async (slug) => { await api.delete(`/admin/categories/${slug}`); toast.success("Silindi"); load(); };
  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Kategoriler</h1>
      <div className="mb-5 grid gap-2 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-5">
        <Input placeholder="Ad" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cat-name" />
        <Input placeholder="slug" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="cat-slug" />
        <Input placeholder="İkon (Package)" value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} />
        <Input placeholder="Alt kategoriler (virgülle)" value={f.subcategories} onChange={(e) => setF({ ...f, subcategories: e.target.value })} />
        <Button onClick={create} disabled={!f.name || !f.slug} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="cat-add"><Plus size={16} /> Ekle</Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((c) => (
          <div key={c.slug} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3">
            <div><p className="font-medium text-slate-800">{c.name}</p><p className="text-xs text-slate-400">{c.slug}</p></div>
            <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(c.slug)} data-testid={`cat-del-${c.slug}`}><Trash2 size={15} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminBuyRequests() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/buy-requests").then(({ data }) => setItems(data.items)); }, []);
  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Alım Talepleri</h1>
      {items.length === 0 ? <EmptyState title="Alım talebi yok" icon={ClipboardList} /> : (
        <div className="space-y-2">
          {items.map((b) => (
            <Link key={b.buy_request_id} to={`/alim-talepleri/${b.buy_request_id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 hover:shadow-sm">
              <div><p className="font-semibold text-slate-800">{b.product}</p><p className="text-xs text-slate-500">{b.city || "—"} • {b.quantity || "—"} {b.unit}</p></div>
              <span className="text-xs text-slate-400">{timeAgo(b.created_at)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminReports() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/reports").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const resolve = async (id, status) => { await api.patch(`/admin/reports/${id}`, { status }); toast.success("Güncellendi"); load(); };
  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Şikayetler</h1>
      {items.length === 0 ? <EmptyState title="Şikayet yok" icon={Flag} /> : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.report_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`report-${r.report_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Link to={`/ilan/${r.listing_id}`} className="font-medium text-slate-800 hover:text-emerald-700">{r.listing_title || r.listing_id}</Link>
                  <p className="text-sm text-red-600">{r.reason}</p>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold", r.status === "acik" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600")}>{r.status === "acik" ? "Açık" : r.status === "cozuldu" ? "Çözüldü" : "Reddedildi"}</span>
              </div>
              {r.status === "acik" && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => resolve(r.report_id, "cozuldu")} data-testid={`resolve-${r.report_id}`}>Çözüldü</Button>
                  <Button size="sm" variant="outline" onClick={() => resolve(r.report_id, "reddedildi")}>Reddet</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminMessages() {
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { api.get("/admin/conversations").then(({ data }) => setConvs(data.items)); }, []);
  const open = (id) => { setActive(id); api.get(`/admin/conversations/${id}/messages`).then(({ data }) => setMsgs(data.items)); };
  if (convs === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Mesaj Denetimi</h1>
      <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
        <div className="max-h-[70vh] space-y-2 overflow-y-auto">
          {convs.length === 0 ? <EmptyState title="Konuşma yok" icon={MessageSquare} /> : convs.map((c) => (
            <button key={c.conversation_id} onClick={() => open(c.conversation_id)} className={cn("w-full rounded-lg border p-3 text-left", active === c.conversation_id ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white")}>
              <p className="truncate text-sm font-medium text-slate-800">{c.listing_title}</p>
              <p className="truncate text-xs text-slate-400">{c.last_message}</p>
            </button>
          ))}
        </div>
        <div className="max-h-[70vh] space-y-2 overflow-y-auto rounded-xl border border-slate-200 bg-white p-4">
          {!active ? <p className="text-sm text-slate-400">Bir konuşma seçin</p> : msgs.map((m) => (
            <div key={m.message_id} className="rounded-lg bg-slate-50 p-2 text-sm"><span className="text-slate-700">{m.text}</span><span className="ml-2 text-[10px] text-slate-400">{timeAgo(m.created_at)}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AdminFeatured() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/featured").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const unfeature = async (id) => { await api.patch(`/admin/listings/${id}/feature`, { featured: false }); toast.success("Kaldırıldı"); load(); };
  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Öne Çıkan İlanlar</h1>
      {items.length === 0 ? <EmptyState title="Öne çıkan ilan yok" icon={Star} description="İlan Yönetimi'nden aktif ilanları öne çıkarabilirsiniz." /> : (
        <div className="space-y-2">
          {items.map((l) => (
            <div key={l.listing_id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
              <Link to={`/ilan/${l.listing_id}`} className="flex items-center gap-3">
                <div className="h-12 w-12 overflow-hidden rounded-lg bg-slate-100">{l.images?.[0] && <img src={imageUrl(l.images[0])} alt="" className="h-full w-full object-cover" />}</div>
                <span className="font-medium text-slate-800">{l.title}</span>
              </Link>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => unfeature(l.listing_id)}><StarOff size={14} /> Kaldır</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
